import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Flex,
  Heading,
  Image,
  Text,
  VStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Link,
  Button,
  SimpleGrid,
  Icon,
  useColorModeValue,
  ScaleFade,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useDisclosure,
} from '@chakra-ui/react';
import { COLORS, GRADIENTS } from '../utils/constants';

// Simple icons
const CoinIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <path d="M12 6V18M8 10H16M8 14H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const AiIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 4V20M18 10H6M18 14H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const WalletIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M22 10H18C16.9 10 16 10.9 16 12C16 13.1 16.9 14 18 14H22V10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const QuestionIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <path d="M12 16V16.5M12 7C10.3431 7 9 8.34315 9 10C9 11.3 9.84 12.4 11 12.83V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

function Help() {
  const [isVisible, setIsVisible] = useState(false);
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  
  // Animation on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);
  
  // FAQ categories and questions
  const faqData = [
    {
      category: "Getting Started",
      questions: [
        {
          question: "What is Ignita?",
          answer: "Ignita is a crypto-native AI image generator that uses XGCL cryptocurrency for payments. Our platform allows you to create unique, high-quality AI-generated images using text prompts."
        },
        {
          question: "How do I create my first image?",
          answer: "To create your first image, you need to sign up for an account, connect your XAMAN wallet, purchase credits with XGCL, and then use our Generate page to enter your text prompt. Our AI will generate an image based on your description."
        },
        {
          question: "Do I need technical knowledge to use Ignita?",
          answer: "No technical knowledge is required! Our user-friendly interface makes it easy for anyone to generate AI images. You simply need to provide a text description of what you want to create."
        }
      ]
    },
    {
      category: "Credits & Payments",
      questions: [
        {
          question: "How do credits work?",
          answer: "Credits are used to generate images on our platform. Each image generation costs a certain number of credits depending on the image size and quality. Standard images cost 1 credit, HD images cost 2 credits, and Ultra HD images cost 3 credits."
        },
        {
          question: "How do I buy credits?",
          answer: "Credits can be purchased using XGCL cryptocurrency through our Buy Credits page. We offer different packages with bonuses for larger purchases. You'll need a XAMAN wallet to complete the transaction."
        },
        {
          question: "What is the current exchange rate?",
          answer: "Our current exchange rate is 2.5 AI Credits per 1 XGCL. This rate may be subject to change based on market conditions."
        },
        {
          question: "Are there any subscription plans?",
          answer: "We currently operate on a pay-as-you-go model using credits. We may introduce subscription plans in the future based on user demand."
        }
      ]
    },
    {
      category: "Image Generation",
      questions: [
        {
          question: "What types of images can I create?",
          answer: "You can create almost any type of image you can imagine! Landscapes, portraits, abstract art, conceptual designs, product visualizations, and much more. The only limitations are related to our content policy, which prohibits offensive or harmful content."
        },
        {
          question: "How detailed should my prompts be?",
          answer: "More detailed prompts generally produce better results. Try to be specific about style, colors, lighting, mood, and composition. For example, instead of 'a cat', try 'a tabby cat sitting on a windowsill at sunset, golden hour lighting, photorealistic style'."
        },
        {
          question: "What image sizes are available?",
          answer: "We offer three image sizes: Standard (512×512), HD (768×768), and Ultra HD (1024×1024). Larger sizes consume more credits but provide higher resolution and more detailed images."
        },
        {
          question: "What is the creativity level setting?",
          answer: "The creativity level determines how closely the AI follows your prompt versus taking creative liberties. A lower setting produces more predictable results that closely match your prompt, while a higher setting allows the AI to be more creative and potentially surprising."
        }
      ]
    },
    {
      category: "XAMAN Wallet",
      questions: [
        {
          question: "What is XAMAN?",
          answer: "XAMAN is a non-custodial wallet for the XRP Ledger that allows you to securely store and manage your XGCL and other XRPL tokens. It's the wallet we use for all cryptocurrency transactions on our platform."
        },
        {
          question: "How do I set up a XAMAN wallet?",
          answer: "To set up a XAMAN wallet, download the XAMAN app from your app store, create a new account, and securely save your recovery phrase. You can then use this wallet to purchase and manage your XGCL tokens."
        },
        {
          question: "Is my wallet secure?",
          answer: "Yes, XAMAN is a non-custodial wallet, which means you have full control of your private keys and funds. We never have access to your wallet's private keys or recovery phrase."
        }
      ]
    },
    {
      category: "Account & Settings",
      questions: [
        {
          question: "How do I change my profile information?",
          answer: "You can change your profile information by going to the Profile page and clicking the 'Edit Profile' button. There you can update your name, username, email, and bio."
        },
        {
          question: "Can I delete my account?",
          answer: "Yes, you can delete your account from the Settings page. Please note that this action is permanent and will remove all your data including generated images and remaining credits."
        },
        {
          question: "How do I view my transaction history?",
          answer: "Your transaction history can be viewed on the Profile page under the 'Transactions' tab. This shows all your credit purchases and usage."
        }
      ]
    }
  ];
  
  return (
    <Container maxW="4xl" py={8}>
      <ScaleFade in={isVisible} initialScale={0.95}>
        <VStack spacing={8} align="stretch">
          <Flex align="center" mb={2}>
            <Image 
              src="/IgnitaLogo.svg" 
              alt="Ignita Logo" 
              height="40px"
              mr={3}
            />
            <Heading 
              as="h1" 
              size="xl" 
              bgGradient={GRADIENTS.primary}
              bgClip="text"
              className="glow-text"
              letterSpacing="tight"
              fontFamily="Space Grotesk"
            >
              Help & FAQ
            </Heading>
          </Flex>
          
          <Text color={COLORS.textSecondary} fontSize="lg">
            Find answers to common questions about using Ignita
          </Text>
          
          {isOpen && (
            <Alert 
              status="info" 
              variant="subtle"
              bg="rgba(30, 220, 11, 0.1)"
              borderWidth="1px"
              borderColor="rgba(30, 220, 11, 0.3)"
              borderRadius="md"
              mb={6}
            >
              <AlertIcon color={COLORS.accent} />
              <Box flex="1">
                <AlertTitle color={COLORS.text}>New to Ignita?</AlertTitle>
                <AlertDescription color={COLORS.textSecondary}>
                  Check out our getting started section below or visit our tutorial page for step-by-step guides.
                </AlertDescription>
              </Box>
              <Button
                onClick={onClose}
                size="sm"
                className="outline-button"
              >
                Dismiss
              </Button>
            </Alert>
          )}
          
          {/* Quick Help Categories */}
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6} mb={8}>
            <Box 
              bg="rgba(0, 0, 0, 0.6)" 
              p={5} 
              borderRadius="lg"
              borderWidth="1px"
              borderColor={COLORS.primary}
              className="card-hover glow-border"
              textAlign="center"
            >
              <Flex 
                w="50px" 
                h="50px" 
                borderRadius="full" 
                bg="rgba(30, 220, 11, 0.1)" 
                align="center" 
                justify="center"
                color={COLORS.accent}
                mx="auto"
                mb={3}
              >
                <Icon as={AiIcon} boxSize={6} />
              </Flex>
              <Heading size="md" mb={2} className="glow-text">Using AI</Heading>
              <Text color={COLORS.textSecondary} fontSize="sm">
                Learn how to create stunning AI images using text prompts
              </Text>
            </Box>
            
            <Box 
              bg="rgba(0, 0, 0, 0.6)" 
              p={5} 
              borderRadius="lg"
              borderWidth="1px"
              borderColor={COLORS.primary}
              className="card-hover glow-border"
              textAlign="center"
            >
              <Flex 
                w="50px" 
                h="50px" 
                borderRadius="full" 
                bg="rgba(30, 220, 11, 0.1)" 
                align="center" 
                justify="center"
                color={COLORS.accent}
                mx="auto"
                mb={3}
              >
                <Icon as={CoinIcon} boxSize={6} />
              </Flex>
              <Heading size="md" mb={2} className="glow-text">Credits</Heading>
              <Text color={COLORS.textSecondary} fontSize="sm">
                Understand how our credit system works for generating images
              </Text>
            </Box>
            
            <Box 
              bg="rgba(0, 0, 0, 0.6)" 
              p={5} 
              borderRadius="lg"
              borderWidth="1px"
              borderColor={COLORS.primary}
              className="card-hover glow-border"
              textAlign="center"
            >
              <Flex 
                w="50px" 
                h="50px" 
                borderRadius="full" 
                bg="rgba(30, 220, 11, 0.1)" 
                align="center" 
                justify="center"
                color={COLORS.accent}
                mx="auto"
                mb={3}
              >
                <Icon as={WalletIcon} boxSize={6} />
              </Flex>
              <Heading size="md" mb={2} className="glow-text">XAMAN Wallet</Heading>
              <Text color={COLORS.textSecondary} fontSize="sm">
                Guide to setting up and using XAMAN for payments
              </Text>
            </Box>
            
            <Box 
              bg="rgba(0, 0, 0, 0.6)" 
              p={5} 
              borderRadius="lg"
              borderWidth="1px"
              borderColor={COLORS.primary}
              className="card-hover glow-border"
              textAlign="center"
            >
              <Flex 
                w="50px" 
                h="50px" 
                borderRadius="full" 
                bg="rgba(30, 220, 11, 0.1)" 
                align="center" 
                justify="center"
                color={COLORS.accent}
                mx="auto"
                mb={3}
              >
                <Icon as={QuestionIcon} boxSize={6} />
              </Flex>
              <Heading size="md" mb={2} className="glow-text">Support</Heading>
              <Text color={COLORS.textSecondary} fontSize="sm">
                Contact our team for personalized assistance
              </Text>
            </Box>
          </SimpleGrid>
          
          {/* FAQ Accordion */}
          <Box 
            bg="rgba(0, 0, 0, 0.6)" 
            p={6} 
            borderRadius="lg"
            borderWidth="1px"
            borderColor={COLORS.primary}
            className="glow-border"
            boxShadow="0 8px 32px rgba(0, 0, 0, 0.3)"
            backdropFilter="blur(10px)"
          >
            <Heading size="lg" mb={6} className="glow-text">Frequently Asked Questions</Heading>
            
            <Accordion allowMultiple defaultIndex={[0]}>
              {faqData.map((category, categoryIndex) => (
                <Box key={categoryIndex} mb={6}>
                  <Heading size="md" mb={4} color={COLORS.accent}>
                    {category.category}
                  </Heading>
                  
                  {category.questions.map((item, itemIndex) => (
                    <AccordionItem 
                      key={itemIndex}
                      border="none" 
                      mb={3}
                      bg="rgba(0, 0, 0, 0.2)"
                      borderRadius="md"
                      overflow="hidden"
                    >
                      <h2>
                        <AccordionButton
                          py={4}
                          _hover={{ bg: "rgba(30, 220, 11, 0.1)" }}
                          _expanded={{ bg: "rgba(30, 220, 11, 0.15)", color: COLORS.text }}
                        >
                          <Box flex="1" textAlign="left" fontWeight="medium">
                            {item.question}
                          </Box>
                          <AccordionIcon color={COLORS.accent} />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel pb={4} color={COLORS.textSecondary}>
                        {item.answer}
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Box>
              ))}
            </Accordion>
          </Box>
          
          {/* Contact Support */}
          <Box 
            bg="rgba(0, 0, 0, 0.6)" 
            p={6} 
            borderRadius="lg"
            borderWidth="1px"
            borderColor={COLORS.primary}
            className="glow-border"
            boxShadow="0 8px 32px rgba(0, 0, 0, 0.3)"
            backdropFilter="blur(10px)"
            textAlign="center"
          >
            <Heading size="md" mb={3} className="glow-text">Still Need Help?</Heading>
            <Text color={COLORS.textSecondary} mb={4}>
              If you couldn't find the answer to your question, our support team is here to help.
            </Text>
            <Button
              bgGradient={GRADIENTS.primary}
              color={COLORS.text}
              _hover={{
                bgGradient: GRADIENTS.hover,
                transform: 'translateY(-2px)'
              }}
              transition="all 0.3s"
              className="btn-shine"
            >
              Contact Support
            </Button>
          </Box>
          
          {/* Additional Resources */}
          <Box 
            bg="rgba(0, 0, 0, 0.6)" 
            p={6} 
            borderRadius="lg"
            borderWidth="1px"
            borderColor={COLORS.primary}
            className="glow-border"
            boxShadow="0 8px 32px rgba(0, 0, 0, 0.3)"
            backdropFilter="blur(10px)"
          >
            <Heading size="md" mb={4} className="glow-text">Additional Resources</Heading>
            
            <VStack align="stretch" spacing={3}>
              <Link 
                color={COLORS.accent}
                fontWeight="medium"
                _hover={{ textDecoration: "none", color: COLORS.text }}
              >
                Video Tutorial: Creating Your First AI Image
              </Link>
              
              <Link 
                color={COLORS.accent}
                fontWeight="medium"
                _hover={{ textDecoration: "none", color: COLORS.text }}
              >
                Guide: Advanced Prompt Engineering
              </Link>
              
              <Link 
                color={COLORS.accent}
                fontWeight="medium"
                _hover={{ textDecoration: "none", color: COLORS.text }}
              >
                XAMAN Wallet Documentation
              </Link>
              
              <Link 
                color={COLORS.accent}
                fontWeight="medium"
                _hover={{ textDecoration: "none", color: COLORS.text }}
              >
                Terms of Service & Content Policy
              </Link>
            </VStack>
          </Box>
        </VStack>
      </ScaleFade>
    </Container>
  );
}

export default Help; 